import { SagaIterator } from 'redux-saga'
import { call, put, takeEvery } from 'redux-saga/effects'

import { store } from '../root/index'
import { fetchScreen, LoadScreenFromNetworkResult, ScreenRequestParams } from '../api/actions'
import { Screen } from '../api/types'
import {
	loadAudioPath,
	loadScreen,
	LoadScreenAction,
	loadedScreenFromDisk,
	LoadedFilePathAction,
	loadVideoPath,
	removeScreenFromDisk,
	loadedAudioPath,
	loadedVideoPath,
	loadFilePathError,
} from './actions'
import { FileType } from './types'
import { getConfiguration } from '../configuration/index'

/**
 * Triggers the loading of a screen
 * @param action contains the id of screen to load
 */
function* handleLoadScreen(action: LoadScreenAction): SagaIterator {
	const { id, version, language, accountId } = action.payload
	const state = store.getState()
	const projectId = state.settings.appInfo.projectId
	const platform = getConfiguration().getPlatform()
	const deviceUUID = getConfiguration().getDeviceUUID()

	// check if we have the json file on disk
	const item: Screen | undefined = yield call(getConfiguration().loadScreenFromDisk, projectId, id, version, language)
	if (item) {
		// store onto redux state
		yield put(loadedScreenFromDisk({ id, version, language, screen: item }))
	} else {
		// else pull down json from network
		// send the async action to start the network request
		const params: ScreenRequestParams = {
			devicePlatform: platform,
			deviceUdid: deviceUUID,
			screenId: id,
			screenVersion: version,
			accountId,
			language,
			isPreCaching: false,
		}
		yield put(fetchScreen.started(params))
	}
}

/**
 * After a successful request to fetch a screen we want to store the result to disk so that we do not need to hit the network again (unless the item version changes).
 * @param action contains the screen retrieved from network response
 */
function* handleLoadedScreenFromNetwork(action: LoadScreenFromNetworkResult): SagaIterator {
	const { screenId, screenVersion, language } = action.payload.params
	const item: Screen = action.payload.result
	const state = store.getState()
	const projectId = state.settings.appInfo.projectId

	// first delete any old screen versions from disk
	try {
		yield call(getConfiguration().removeScreenFromDisk, projectId, screenId, language)
	} catch (error) {
		// Error deleting old data
	}

	try {
		yield call(getConfiguration().saveScreenToDisk, projectId, screenId, screenVersion, language, item)
	} catch (error) {
		// Error saving data
	}
}

/**
 * Handles removing a screen from disk
 * @param action contains the id of screen to remove
 */
function* handleRemovingScreenFromDisk(action: LoadScreenAction): SagaIterator {
	const { id, language } = action.payload
	const state = store.getState()
	const projectId = state.settings.appInfo.projectId

	try {
		// remove json from disk if we have it
		yield call(getConfiguration().removeScreenFromDisk, projectId, id, language)
	} catch (error) {
		// Error deleting old data
	}
}

/**
 * Handles loading a AUDIO file path either from disk, if it exists, otherwise downloads it from the network.
 * @param action contains the params needed to build the audio file path
 */
function* handleLoadFilePath(action: LoadedFilePathAction): SagaIterator {
	const { type, fileName, url } = action.payload
	const filePath = getConfiguration().getFileDownloadPath(type, fileName)
	const fileExists = yield call(getConfiguration().checkFileExists, filePath)
	if (fileExists) {
		yield put(getLoadedFilePathAction(type, fileName, filePath))
	} else {
		try {
			const downloadedFilePath = yield call(getConfiguration().downloadFile, type, fileName, url)
			yield put(getLoadedFilePathAction(type, fileName, downloadedFilePath))
		} catch (error) {
			yield put(getLoadedFilePathAction(type))
		}
	}
}

function getLoadedFilePathAction(type: FileType, fileName?: string, filePath?: string) {
	switch (type) {
		case FileType.AUDIO:
			return loadedAudioPath({ fileName: fileName!, filePath: filePath! })
		case FileType.VIDEO:
			// FIXME - prepend filepath when actually using the path instead of storing modified version on redux
			// for video files on android we need to prefix the path with file:// for it to load correctly
			// filePath = Platform.OS === 'android' ? 'file://' + filePath : filePath
			return loadedVideoPath({ fileName: fileName!, filePath: filePath! })
		default:
			return loadFilePathError()
	}
}

export default function*(): SagaIterator {
	yield takeEvery(loadScreen, handleLoadScreen)

	// when item loaded from network, store it to disk
	yield takeEvery(fetchScreen.done, handleLoadedScreenFromNetwork)

	yield takeEvery(removeScreenFromDisk, handleRemovingScreenFromDisk)

	// loads the audio file path if exists or downloads from the network
	yield takeEvery(loadAudioPath, handleLoadFilePath)

	// loads the video file path if exists or downloads from the network
	yield takeEvery(loadVideoPath, handleLoadFilePath)
}
